import React from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Home, Mail, Sparkles, Zap, CheckCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';

const EmailGenerator = () => {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100">
      <div className="bg-white border-b shadow-sm sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <Button variant="ghost" onClick={() => navigate('/documentation')} className="flex items-center gap-2">
              <ArrowLeft className="w-4 h-4" />
              Back to Docs
            </Button>
            <Button variant="outline" onClick={() => navigate('/')} className="flex items-center gap-2">
              <Home className="w-4 h-4" />
              Home
            </Button>
          </div>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="mb-8">
          <div className="flex items-center gap-2 mb-4">
            <Mail className="w-8 h-8 text-blue-600" />
            <h1 className="text-4xl font-bold text-slate-900">AI Intro Email Generator</h1>
          </div>
          <p className="text-lg text-slate-600">Generate personalized, compelling introduction emails powered by AI in seconds.</p>
        </div>

        <div className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>Overview</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <p className="text-slate-700">
                The AI Email Generator creates personalized introduction emails for your leads using enriched data and advanced language models. Each email is tailored to the lead's industry, role, and company context.
              </p>
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <div className="flex gap-2">
                  <Sparkles className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
                  <p className="text-sm text-slate-700">
                    <strong>AI-Powered Personalization:</strong> The system analyzes lead data including company info, job title, industry, and recent activities to craft relevant messages.
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Zap className="w-5 h-5 text-yellow-600" />
                How to Generate an Email
              </CardTitle>
            </CardHeader>
            <CardContent>
              <ol className="list-decimal list-inside space-y-3 text-slate-700">
                <li>Open a lead detail page</li>
                <li>Click the "Generate AI Email" button in the actions section</li>
                <li>Review the AI-generated email draft</li>
                <li>Edit or customize the email as needed</li>
                <li>Copy and send via your preferred email client</li>
              </ol>
              <div className="mt-4 p-4 bg-slate-50 rounded-lg">
                <p className="text-sm text-slate-600">
                  <strong>Note:</strong> The quality of AI-generated emails improves with more complete lead enrichment data.
                </p>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <CheckCircle className="w-5 h-5 text-green-600" />
                Best Practices
              </CardTitle>
            </CardHeader>
            <CardContent>
              <ul className="list-disc list-inside space-y-2 text-slate-700">
                <li>Always review and personalize AI-generated emails before sending</li>
                <li>Enrich leads with latest data for better email quality</li>
                <li>Add your own unique value propositions and call-to-actions</li>
                <li>Test different email styles and track response rates</li>
                <li>Use email templates for consistent branding</li>
              </ul>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Sample Generated Email</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="bg-slate-50 border border-slate-200 rounded-lg p-4 font-mono text-sm">
                <p className="mb-2"><strong>Subject:</strong> Quick question about [Company]'s growth plans</p>
                <p className="mb-4"><strong>Body:</strong></p>
                <p className="text-slate-700 leading-relaxed">
                  Hi [First Name],<br /><br />
                  I noticed [Company] has been expanding rapidly in the [Industry] space. As someone focused on [Job Function], I imagine you're looking for ways to streamline [relevant pain point].<br /><br />
                  We've helped similar companies in [Industry] achieve [specific benefit]. Would you be open to a brief conversation about how we might support your team's goals?<br /><br />
                  Best regards,<br />
                  [Your Name]
                </p>
              </div>
            </CardContent>
          </Card>

          <div className="flex gap-3 pt-6">
            <Button onClick={() => navigate('/documentation')} className="bg-indigo-600 hover:bg-indigo-700">
              Browse All Documentation
            </Button>
            <Button variant="outline" onClick={() => navigate('/contact')}>
              Contact Support
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EmailGenerator;
